import { useState } from "react";

import { Container, Grid, Text, Title } from "@mantine/core";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useCTA } from "../../hooks/useCTA";
import { useCountEvent } from "../../hooks/useCountEvent";
import {
  AddonTemplate,
  isAircraftAddonTemplate,
  isSoftwareAddonTemplate,
} from "../../lib/model/addonTemplate";
import { aircraftAddonTemplates, softwareAddonTemplates } from "../../lib/state/addonTemplates";
import { librarySlice } from "../../lib/state/reducer";
import { AddonPreviewModal } from "../addons/AddonPreviewModal";
import { AddonTemplateCard } from "../addons/AddonTemplateCard";

export const TemplateGalleryPage = (): JSX.Element => {
  const [previewTemplate, setPreviewTemplate] = useState<AddonTemplate | null>(null);

  return (
    <Container size={"xl"} py="md">
      <Helmet>
        <title>Template Gallery</title>
      </Helmet>
      <AddonPreviewModal
        template={previewTemplate}
        open={previewTemplate !== null}
        onClose={() => setPreviewTemplate(null)}
      />
      <Container size="md">
        <Title order={1}>Flight Simulator Addon Templates</Title>
        <Text mt="md">
          Get a head start on your checklist by starting from one of our templates for dozens of
          flight simulator add-on products. Then customize as you see fit!
        </Text>
      </Container>
      <Container size="md">
        <Title order={2} mt={"xl"} mb={"sm"}>
          Aircraft
        </Title>
      </Container>
      <TemplateSection
        templates={aircraftAddonTemplates}
        setPreviewTemplate={(t) => setPreviewTemplate(t)}
        linkToDetailView
      />
      <Container size="md">
        <Title order={2} mt={"xl"} mb={"sm"}>
          Addons
        </Title>
      </Container>
      <TemplateSection
        templates={softwareAddonTemplates}
        setPreviewTemplate={(t) => setPreviewTemplate(t)}
      />
    </Container>
  );
};

const TemplateSection = ({
  templates,
  setPreviewTemplate,
  linkToDetailView,
}: {
  templates: AddonTemplate[];
  setPreviewTemplate: (addonTemplate: AddonTemplate) => void;
  linkToDetailView?: boolean;
}): JSX.Element => {
  const navigate = useNavigate();
  const reduxDispatch = useDispatch();
  const countEvent = useCountEvent();

  const cta = useCTA();

  const onSelect = (t: AddonTemplate) => {
    if (isAircraftAddonTemplate(t)) {
      reduxDispatch(
        librarySlice.actions.newDocumentFromTemplate({ baseTemplate: t, softwareAddons: [] })
      );
    } else if (isSoftwareAddonTemplate(t)) {
      reduxDispatch(librarySlice.actions.insertContentsFromTemplate(t));
    }
    navigate("/editor");
  };
  return (
    <Grid>
      {templates.map((template) => {
        return (
          <Grid.Col xl={2} lg={3} md={3} sm={6} xs={12} key={template.id}>
            <AddonTemplateCard
              addonTemplate={template}
              onPreview={() => {
                setPreviewTemplate(template);
                countEvent("preview-template-gallery", template.id);
              }}
              primaryActionLabel={cta(template)}
              primaryAction={() => {
                onSelect(template);
                countEvent("start-from-template-gallery", template.id);
              }}
              linkToDetailView={linkToDetailView}
            />
          </Grid.Col>
        );
      })}
    </Grid>
  );
};
